import styled, { css } from 'styled-components';

import { Flex } from '@/components/Flex';
import type { ContentfulCardFragment } from '@/graphql/contentful/api';
import type { Maybe } from '@/lib/types';

import { getContentAlignment, visibilityControl } from '../../Card.styles';

export const Wrapper = styled(Flex)<
  Pick<
    ContentfulCardFragment,
    | 'contentAlignment'
    | 'mediaAnimationEffect'
    | 'visibleInDesktop'
    | 'visibleInMobile'
  > & {
    $desktopAspectRatio: { x: number; y: number };
    $hasBackgroundImage: boolean;
    $mediaWrapperType: Maybe<string>;
    $mobileAspectRatio: { x: number; y: number };
  }
>`
  position: relative;
  overflow: hidden;
  width: 100%;
  ${({ visibleInDesktop, visibleInMobile }) => css`
    ${visibilityControl(visibleInDesktop, visibleInMobile)}
  `};
  ${({ $desktopAspectRatio, $mobileAspectRatio, theme }) => css`
    aspect-ratio: ${$mobileAspectRatio.x} / ${$mobileAspectRatio.y};
    img,
    video {
      aspect-ratio: ${$mobileAspectRatio.x} / ${$mobileAspectRatio.y};
    }

    ${theme.media.md.up} {
      aspect-ratio: ${$desktopAspectRatio.x} / ${$desktopAspectRatio.y};
      img,
      video {
        aspect-ratio: ${$desktopAspectRatio.x} / ${$desktopAspectRatio.y};
      }
    }
  `}

  ${({
    $hasBackgroundImage,
    contentAlignment,
    mediaAnimationEffect,
    theme,
  }) => css`
    ${mediaAnimationEffect &&
    css`
      &:hover {
        cursor: pointer;
        img {
          transform: scale(1.1);
          transition: all 0.5s linear;
        }
      }
    `}

    padding: ${theme.spacing(2, 2)};
    color: ${$hasBackgroundImage ? theme.colors.white : theme.colors.black};

    ${theme.media.md.up} {
      padding: ${({ theme }) => theme.spacing(5)} 5%;
      ${getContentAlignment(contentAlignment)};
    }

    ${theme.media.lg.up} {
      padding-inline: 10%;
    }

    ${theme.media.md.down} {
      ${getContentAlignment(contentAlignment)}
    }
  `};

  ${({ $mediaWrapperType, theme }) =>
    $mediaWrapperType === 'MediaBanner' && theme.media.md.down} {
    height: 100%;
  }
`;

export const Content = styled(Flex)<
  Pick<ContentfulCardFragment, 'textAlignment'> & {
    $deskTopContentMaxWidth: string;
    $hasVideo?: boolean;
    $isEdgeBrowser?: boolean;
    $mediaWrapperType: Maybe<string>;
    itemsAlignment: string;
  }
>`
  ${({ $hasVideo }) =>
    $hasVideo &&
    css`
      pointer-events: none;
    `}
  min-height: 100%;
  flex-direction: column;
  max-width: 400px;
  word-break: break-word;
  white-space: pre-wrap;
  z-index: 2;
  ${({ $deskTopContentMaxWidth, itemsAlignment, textAlignment, theme }) => css`
    font-size: ${theme.fontSizes.p2};
    justify-content: ${itemsAlignment};
    ${theme.media.md.down} {
      max-width: 100%;
      width: 100%;
      margin: 0 auto;
      text-align: center;
    }

    ${theme.media.md.up} {
      max-width: ${$deskTopContentMaxWidth ? $deskTopContentMaxWidth : '500px'};
      text-align: ${textAlignment ?? 'center'};
    }

    ${theme.media.lg.up} {
      max-width: ${$deskTopContentMaxWidth ? $deskTopContentMaxWidth : '550px'};
      text-align: ${textAlignment ?? 'center'};
    }
  `}

  ${({ $isEdgeBrowser, $mediaWrapperType, theme }) =>
    $mediaWrapperType === 'MediaBanner' &&
    theme.media.md.down &&
    css`
      ${$isEdgeBrowser
        ? css`
            height: calc(100% - ${theme.spacing(8)});
          `
        : css`
            height: 100%;
          `}
    `}
`;
